import { useState } from 'react'
import {X, KeyRound, RefreshCw, Copy, Check} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { useAppAuth } from '@/contexts/AuthContext'
import { hashPassword, nowISO } from '@/utils/masks'

interface ResetSenhaModalProps {
  usuario: any
  onClose: () => void
  onReset: () => void
}

function gerarSenhaTemporaria() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'
  let senha = ''
  for (let i = 0; i < 10; i++)
    senha += chars[Math.floor(Math.random() * chars.length)]
  return senha
}

function ResetSenhaModal({ usuario, onClose, onReset }: ResetSenhaModalProps) {
  const { user: currentUser } = useAppAuth()
  const [modo, setModo] = useState<'gerar' | 'manual'>('gerar')
  const [senhaGerada, setSenhaGerada] = useState(gerarSenhaTemporaria())
  const [novaSenha, setNovaSenha] = useState('')
  const [confirmarSenha, setConfirmarSenha] = useState('')
  const [saving, setSaving] = useState(false)
  const [copiado, setCopiado] = useState(false)
  const [erro, setErro] = useState('')

  const podeResetar = currentUser?.role === 'ADMIN'

  const handleCopiar = async () => {
    try {
      await navigator.clipboard.writeText(senhaGerada)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2000)
    }
    catch {
      toast.error('Não foi possível copiar a senha.')
    }
  }

  const handleConfirmar = async () => {
    setErro('')
    const senhaFinal = modo === 'gerar' ? senhaGerada : novaSenha

    if (modo === 'manual') {
      if (novaSenha.length < 6) {
        setErro('A senha deve ter no mínimo 6 caracteres.')
        return
      }
      if (novaSenha !== confirmarSenha) {
        setErro('As senhas não coincidem.')
        return
      }
    }

    setSaving(true)
    try {
      const passwordHash = await hashPassword(senhaFinal)
      await (lumi.entities as any).user_profiles.update(usuario._id, {
        passwordHash,
        updatedAt: nowISO(),
      })

      try {
        await (lumi.entities as any).protocolo_historico.create({
          protocoloId: `user:${usuario._id}`,
          acao: 'RESET_SENHA',
          userId: currentUser?.userId || 'system',
          descricao: `Senha redefinida por administrador (${currentUser?.nomeCompleto || 'Admin'}) para o utilizador ${usuario.nomeCompleto}.`,
          creator: currentUser?.userId || 'system',
          createdAt: nowISO(),
        })
      }
      catch {
        /* histórico é best-effort, não deve bloquear o reset */
      }

      toast.success('Senha redefinida com sucesso.')
      onReset()
      if (modo === 'gerar') {
        // mantém modal aberto mostrando a senha para o admin copiar antes de fechar
      }
      else {
        onClose()
      }
    }
    catch {
      toast.error('Erro ao redefinir a senha. Tente novamente.')
    }
    finally {
      setSaving(false)
    }
  }

  if (!podeResetar) return null

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-[460px]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E7E9EC]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#EEF3FE] flex items-center justify-center">
              <KeyRound className="w-4 h-4 text-[#1E4FBF]" strokeWidth={1.75} />
            </div>
            <h2 className="text-[15.5px] font-semibold text-[#12141A]">Redefinir Senha</h2>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#12141A]">
            <X className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </div>

        <div className="p-6 flex flex-col gap-4">
          <p className="text-[13.5px] text-[#6B7280]">
            Definir nova senha para
            {' '}
            <span className="font-medium text-[#12141A]">{usuario?.nomeCompleto}</span>
            .
          </p>

          <div className="flex gap-2 p-1 rounded-lg bg-[#F1F2F4] w-fit">
            <button
              onClick={() => setModo('gerar')}
              className={`px-3.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${modo === 'gerar' ? 'bg-white text-[#12141A] shadow-sm' : 'text-[#6B7280]'}`}
            >
              Gerar automaticamente
            </button>
            <button
              onClick={() => setModo('manual')}
              className={`px-3.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${modo === 'manual' ? 'bg-white text-[#12141A] shadow-sm' : 'text-[#6B7280]'}`}
            >
              Definir manualmente
            </button>
          </div>

          {modo === 'gerar'
            ? (
                <div>
                  <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Senha temporária</label>
                  <div className="flex items-center gap-2">
                    <input
                      value={senhaGerada}
                      readOnly
                      className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] font-mono bg-[#FAFBFC] focus:outline-none"
                    />
                    <button
                      onClick={() => setSenhaGerada(gerarSenhaTemporaria())}
                      title="Gerar nova senha"
                      className="p-2.5 rounded-lg border border-[#D1D5DB] text-[#6B7280] hover:bg-[#F5F6F8] shrink-0"
                    >
                      <RefreshCw className="w-4 h-4" strokeWidth={1.75} />
                    </button>
                    <button
                      onClick={handleCopiar}
                      title="Copiar senha"
                      className="p-2.5 rounded-lg border border-[#D1D5DB] text-[#6B7280] hover:bg-[#F5F6F8] shrink-0"
                    >
                      {copiado ? <Check className="w-4 h-4 text-[#1E8A45]" strokeWidth={1.75} /> : <Copy className="w-4 h-4" strokeWidth={1.75} />}
                    </button>
                  </div>
                  <p className="text-[12px] text-[#9CA3AF] mt-1.5">
                    Copie e informe esta senha ao utilizador. Ela não será exibida novamente após fechar esta janela.
                  </p>
                </div>
              )
            : (
                <>
                  <div>
                    <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Nova Senha</label>
                    <input
                      type="password"
                      value={novaSenha}
                      onChange={e => setNovaSenha(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
                    />
                  </div>
                  <div>
                    <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Confirmar Senha</label>
                    <input
                      type="password"
                      value={confirmarSenha}
                      onChange={e => setConfirmarSenha(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
                    />
                  </div>
                </>
              )}

          {erro && <p className="text-[12.5px] text-[#B91C1C]">{erro}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium border border-[#D1D5DB] hover:bg-[#F5F6F8]">
              Fechar
            </button>
            <button
              onClick={handleConfirmar}
              disabled={saving}
              className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium bg-[#1E4FBF] hover:bg-[#1A44A6] text-white disabled:opacity-60"
            >
              {saving ? 'A redefinir...' : 'Redefinir Senha'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetSenhaModal
